import _ from 'lodash'

import moment from 'moment'

import AuthService from '@/services/retailer/AuthService.js'
import UserService from '@/services/retailer/UserService.js'

import { errorMessage, sma256Hash } from '@/utilities/helpers'

import { NOTIFICATION_TYPES, NOTIFICATION_SEVERITY_TYPES } from '@/constants/constants'
import { AUTH_ERROR_MESSAGES } from '@/constants/errorMessages'
import { SALES_CHANNEL_TYPE } from '@/constants/salesChannels'

export const namespaced = true

// states
export const state = {
  user: null,
  token: null,
  support: null,
  unread_notifications: [],
  unseen_notification_count: '0',
  filter_notifications_by_severities: [],
  show_notification_container: false,
  nextRoute: null
}

export const getters = {
  token: state => {
    return state.token ? state.token : localStorage.getItem('retailer_access_token')
  },
  isDemo: state => {
    return state.user.retailer.is_demo
  },
  retailer: state => state.user.retailer,
  forwardSalesChannelStatus: state => state.user.retailer.sales_channels.some(sales_channel => sales_channel.id === SALES_CHANNEL_TYPE.FORWARD_SALE),
  virtualPosSalesChannelStatus: state => state.user.retailer.sales_channels.some(sales_channel => sales_channel.id === SALES_CHANNEL_TYPE.VIRTUAL_POS),
  proSalesChannelsStatus: state => state.user.retailer.sales_channels.some(sales_channel => [SALES_CHANNEL_TYPE.DIRECT_CASH, SALES_CHANNEL_TYPE.DIRECT_FORWARD].includes(sales_channel.id)),
  fibaSalesChannelStatus: state => state.user.retailer.sales_channels.some(sales_channel => [SALES_CHANNEL_TYPE.FIBABANK].includes(sales_channel.id)),
  getNextRoute: state => {
    return state.nextRoute
  }
}

// mutations
export const mutations = {
  SET_TOKEN(state, token) {
    state.token = token
    localStorage.setItem('retailer_access_token', token)
  },
  LOGOUT(state, path) {
    localStorage.removeItem('retailer_access_token')

    if (path) {
      localStorage.setItem('next_route_retailer', path)
    }
    location.reload()
  },

  SET_USER(state, user) {
    state.user = user
    state.support = {
      ...user.retailer.region_manager,
      support_number: user.retailer.support_number,
      operation_specialist_number: user.retailer.operation_specialist_number
    }
    state.unread_notifications = user.unread_notifications

    Echo.connector.options.auth.headers['Authorization'] = 'Bearer ' + localStorage.getItem('retailer_access_token')
  },
  SET_UNREAD_NOTIFICATIONS(state, notifications) {
    state.unread_notifications = notifications
  },
  SET_UNSEEN_NOTIFICATION_COUNT(state, count) {
    state.unseen_notification_count = count
  },
  SET_FILTER_NOTIFICATIONS_BY_SEVERITIES(state, notifications) {
    state.filter_notifications_by_severities = notifications
  },
  ADD_NOTIFICATION(state, notification) {
    state.unread_notifications.unshift(notification)
    state.unseen_notification_count++
  },
  REMOVE_NOTIFICATION(state, notification) {
    state.unread_notifications = _.reject(state.unread_notifications, item => {
      return item.id === notification.id
    })
  },
  SET_LATEST_PRICE_DATE(state, date) {
    state.user.latest_price_date = date
  },
  SET_NEXT_ROUTE(state, path) {
    state.nextRoute = path
  },
  SET_USER_NOTIFICATIONS(state, notifications) {
    state.unread_notifications = notifications
  },
  SET_SHOW_NOTIFICATION_CONTAINER(state, value) {
    state.show_notification_container = value
  }
}

// actions
export const actions = {
  async getUser({ commit, dispatch, state }, refresh = false) {
    if (state.user && !refresh) {
      state.user.hash_email = await sma256Hash(state.user.email)
      return new Promise(resolve => {
        resolve(state.user)
      })
    }
    return new Promise(resolve => {
      AuthService.getUser().then(async response => {
        response.data.hash_email = await sma256Hash(response.data.email)
        commit('SET_USER', response.data)

        dispatch('subscribeToChannel')

        resolve(response.data)
      })
    })
  },
  login({ commit, dispatch }, user) {
    return new Promise((resolve, reject) => {
      AuthService.login(user)
        .then(async response => {
          response.user.hash_email = await sma256Hash(response.user.email)
          commit('SET_TOKEN', response.access_token)
          commit('SET_USER', response.user)

          dispatch('subscribeToChannel')

          resolve(response.user)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message: AUTH_ERROR_MESSAGES.loginError
          }
          dispatch('notification/add', notification, {
            root: true
          })

          delete localStorage.clear()
          reject(AUTH_ERROR_MESSAGES.loginError)
        })
    })
  },
  logout({ commit, dispatch }) {
    AuthService.logout().then(() => {
      dispatch('unsubscribeFromChannel')
      commit('LOGOUT')
    })
  },
  register({ dispatch }, retailer) {
    return new Promise((resolve, reject) => {
      AuthService.register(retailer)
        .then(() => {
          return dispatch('login', {
            email: retailer.email,
            password: retailer.password
          }).then(r => {
            resolve(r)
          })
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message: `${AUTH_ERROR_MESSAGES.registerError} ${errorMessage(err)}`
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  saveActivities({ commit, state }, data) {
    return new Promise((resolve, reject) => {
      AuthService.saveActivities(data)
        .then(() => {
          resolve(true)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message: `${AUTH_ERROR_MESSAGES.registerError} ${errorMessage(err)}`
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  forgotPassword({ dispatch }, retailer) {
    return new Promise((resolve, reject) => {
      AuthService.forgotPassword(retailer)
        .then(response => {
          resolve(response)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message: AUTH_ERROR_MESSAGES.forgotPasswordError
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  resetPassword({ dispatch }, retailer) {
    return new Promise((resolve, reject) => {
      AuthService.resetPassword(retailer)
        .then(response => {
          resolve(response)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message: AUTH_ERROR_MESSAGES.resetPasswordError
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  getNotifications({ commit, state }) {
    return new Promise((resolve, reject) => {
      UserService.getNotifications(state.user.id)
        .then(response => {
          commit('SET_UNREAD_NOTIFICATIONS', response.data)
          commit('SET_UNSEEN_NOTIFICATION_COUNT', response.meta.unseen_notification_count)
          resolve(response.data)
        })
        .catch(() => reject())
    })
  },
  filterNotificationsBySeverities({ commit, state }) {
    return new Promise((resolve, reject) => {
      UserService.filterNotificationsBySeverities(state.user.id)
        .then(response => {
          commit('SET_FILTER_NOTIFICATIONS_BY_SEVERITIES', response.data)
          resolve(response.data)
        })
        .catch(() => reject())
    })
  },
  markAsReadNotification({ commit, state }, id) {
    return UserService.markAsReadNotification(state.user.id, id).then(() => {
      commit('REMOVE_NOTIFICATION', id)
    })
  },
  markAsSeenNotifications({ commit, state }) {
    UserService.markAsSeenNotifications(state.user.id).then(() => {
      commit('SET_UNSEEN_NOTIFICATION_COUNT', 0)
    })
  },
  addNotification({ commit }, notification) {
    let content = {
      data: {},
      created_at: moment()
    }
    _.forEach(notification, (n, k) => {
      if (k == 'id' || k == 'type') {
        content[k] = n
      } else {
        content.data[k] = n
      }
    })
    commit('ADD_NOTIFICATION', content)
  },
  acceptPolicy({ dispatch }) {
    return new Promise((resolve, reject) => {
      AuthService.acceptPolicy()
        .then(response => {
          resolve(response.data)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message: AUTH_ERROR_MESSAGES.policyError + ' ' + errorMessage(err)
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  setUserNotifications({ commit, dispatch }, notifications) {
    commit('SET_USER_NOTIFICATIONS', notifications)
  },
  setShowNotificationContainer({ commit }, value) {
    commit('SET_SHOW_NOTIFICATION_CONTAINER', value)
  },
  subscribeToChannel({ state, dispatch }) {
    const notificationDefaults = {
      severity: NOTIFICATION_SEVERITY_TYPES.INFO,
      is_dismissible: true,
      type: NOTIFICATION_TYPES.TOAST
    }

    Echo.private('App.Models.RetailerUser.' + state.user.id).notification(notification => {
      const notificationContent = {
        ...notificationDefaults,
        ...notification.data,
        id: notification.id
      }

      dispatch('notification/add', notificationContent, {
        root: true
      })
      dispatch('addNotification', notification.data)
    })
  },
  unsubscribeFromChannel({ state }) {
    Echo.leave('App.Models.RetailerUser.' + state.user.id)
  }
}
